import React, { useMemo, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { toast } from "react-toastify";
import LoadingOverlay from "../common/LoadingOverlay";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const norm = (s) =>
    (s ?? "")
        .toString()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();
const statusLabels = {
    0: "Por Clasificar",
    1: "Error",
    2: "Pendiente",
    3: "Retenido",
    4: "Cancelado",
    5: "Duplicado",
    6: "En Aclaración",
    10: "Clasificado",
};

const statusColors = {
    0: "bg-green-100 text-green-700",
    1: "bg-red-100 text-red-700",
    2: "bg-gray-100 text-gray-700",
    3: "bg-purple-100 text-purple-700",
    4: "bg-red-100 text-red-700",
    5: "bg-pink-100 text-pink-700",
    6: "bg-orange-100 text-orange-700",
    10: "bg-blue-100 text-blue-700",
};

const DmeLogTable = ({
    items = [],           // todos los elementos (sin paginar)
    summary = { successCount: 0, errorCount: 0, naCount: 0 },
    loading,
    onDetail,
    // paginación/búsqueda client-side
    page = 1,
    setPage = () => { },
    pageSize = 25,
    setPageSize = () => { },
    q = "",
    setQ = () => { },
    userEmail = "",
    chain = "",           
    from = null,          
    to = null
}) => {
    const { get, post } = useApi();
    const [modalOpen, setModalOpen] = useState(false);
    const [modalReturnId, setModalReturnId] = useState(null);
    const [modalFolios, setModalFolios] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [loading2, setLoading2] = useState(false);
    const [exporting, setExporting] = useState(false);

    const fetchPendingFolios = async (returnId) => {
        setModalLoading(true);
        try {
            const res = await get("LimiteFoliosPendientes", { params: { returnId }, showError: false });
            setModalFolios(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error("Error fetching pending folios:", err);
            toast.error("No se pudo obtener folios pendientes.");
            setModalFolios([]);
        } finally {
            setModalLoading(false);
        }
    };

    const openModalFor = async (returnId) => {
        setModalReturnId(returnId);
        setModalOpen(true);
        await fetchPendingFolios(returnId);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalReturnId(null);
        setModalFolios([]);
    };

    const handleAuthorize = async (reference) => {
        if (!modalReturnId || !reference) return;
        if (!window.confirm(`Autorizar folio ${reference}?`)) return;

        setLoading2(true);
        try {
            await post("apruebaLimiteDME", {
                returnId: modalReturnId,
                reference,
                userEmail
            }, { showError: false });

            toast.success("Folio autorizado.");
            await fetchPendingFolios(modalReturnId);
            onDetail && onDetail(modalReturnId);
        } catch (err) {
            console.error("AuthorizeLimitFolio error:", err);
            const msg = err?.response?.data?.message || "Error autorizando folio";
            toast.error(msg);
        }
        finally {
            setLoading2(false);
        }
    };

    const handleReject = async (reference) => {
        if (!modalReturnId || !reference) return;
        if (!window.confirm(`Rechazar folio ${reference}?`)) return;
        setLoading2(true);
        try {
            await post("rechazaLimiteDME", {
                returnId: modalReturnId,
                reference,
                userEmail
            }, { showError: false });

            toast.success("Folio cancelado.");
            await fetchPendingFolios(modalReturnId);
            onDetail && onDetail(modalReturnId);
        } catch (err) {
            console.error("RejectLimitFolio error:", err);
            const msg = err?.response?.data?.message || "Error rechazando folio";
            toast.error(msg);
        }
        finally {
            setLoading2(false);
        }
    };

    /*const exportToExcel = () => {
        try {
            setExporting(true);
            // Exporta los registros filtrados; si quieres solo la página usa `pageRows` en lugar de `filtered`
            const source = filtered; // o pageRows

            const data = source.map((r) => {
                const error = r.error ?? r.Error ?? 0;
                const statusText = error === 0 ? "Carga completa" : "Pendientes por procesar";


                return {
                    ID: r.returnId,
                    Cadena: r.chain,
                    Archivo: r.fileName,
                    Fecha: r.date ? new Date(r.date).toLocaleString() : "",
                    Estatus: statusText,
                    Cargado: r.byClassify ?? r.ByClassify ?? 0,
                    Error: r.error ?? r.Error ?? 0,
                    Retenido: r.retained ?? r.Retained ?? 0,
                    Rechazado: r.rejected ?? r.Rejected ?? 0,
                    Duplicado: r.duplicate ?? r.Duplicate ?? 0,
                    EnAclaracion: r.inClarification ?? r.InClarification ?? 0,
                    Clasificado: r.classified ?? r.Classified ?? 0,
                };
            });

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "DME_Logs");

            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/octet-stream" });
            //const name = `dme_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.xlsx`;
            const pad = (n) => String(n).padStart(2, "0");
            const now = new Date();
            const name = `dme_logs_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}.xlsx`;
            saveAs(blob, name);
        } catch (err) {
            console.error("Export to Excel failed:", err);
            toast.error("No se pudo exportar a Excel.");
        } finally {
            setExporting(false);
        }
    };*/

    const exportToExcel = async () => {
        try {
            setExporting(true);

            // Llamar al endpoint backend
            const response = await get("ExportLogs", {
                params: { chain, from, to }
            });

            //console.log(response);
            const exportData = [];
            let totalGeneral = 0;

            // Por cada archivo
            response.logs.forEach((log) => {
                // Fila del archivo
                /*exportData.push({
                    //Tipo: "ARCHIVO",
                    ReturnId: log.returnId,
                    Cadena: log.chain,
                    Archivo: log.fileName,
                    Fecha: new Date(log.date).toLocaleString(),
                    Estatus: log.status === 0 ? "Carga completa" : "Pendientes",
                    "En Archivo": log.byClassify,
                    Error: log.error,
                    Duplicado: log.duplicate,
                    "Por Clasificar": log.byClassify,
                    Retenido: log.retained,
                    "En Aclaracion": log.inClarification,
                    Cancelado: log.rejected,
                    Clasificado: log.classified,
                });*/

                // Filas de detalles
                log.details.forEach((detail) => {
                    exportData.push({
                        //Tipo: "DETALLE",
                        /*ReturnId: "",
                        Cadena: "",
                        Archivo: "",
                        Fecha: "",
                        Estatus: "",
                        "En Archivo": "",
                        Error: "",
                        Duplicado: "",
                        "Por Clasificar": "",
                        Retenido: "",
                        "En Aclaracion": "",
                        Cancelado: "",
                        Clasificado: "",*/
                        ReturnId: log.returnId,
                        Cadena: log.chain,
                        Archivo: log.fileName,
                        Fecha: new Date(log.date).toLocaleString(),
                        /*Estatus: log.status === 0 ? "Carga completa" : "Pendientes",*/
                        "En Archivo": log.byClassify,
                        Error: log.error,
                        Duplicado: log.duplicate,
                        "Por Clasificar": log.byClassify,
                        Retenido: log.retained,
                        "En Aclaracion": log.inClarification,
                        Cancelado: log.rejected,
                        Clasificado: log.classified,
                        Folio: detail.reference || "",
                        Cliente: detail.branch || "",
                        "Cliente QA": detail.clientQA || "",
                        Nombre: detail.clientName || "",
                        Sku: detail.sku || "",
                        "Sku QA": detail.skuQA || "", 
                        Descripcion: detail.skuDescription || "",
                        UM: detail.mu || "",
                        Cantidad: detail.quantity?.toFixed(2) || "0.00",
                        Precio: detail.price?.toFixed(2) || "0.00",
                        Total: detail.total?.toFixed(2) || "0.00",
                        "Estatus Folio": detail.statusText || "",
                        "Error Folio": detail.error || "",
                    });
                    //totalGeneral += detail.total || 0;
                });

                // Línea en blanco
                //exportData.push({});
            });

            // Total general
            /*exportData.push({
                Tipo: "TOTAL GENERAL",
                Total: totalGeneral.toFixed(2)
            });*/

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "DME_Logs_Completo");

            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/octet-stream" });
            const pad = (n) => String(n).padStart(2, "0");
            const now = new Date();
            const name = `dme_logs_completo_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.xlsx`;
            saveAs(blob, name);
        } catch (err) {
            console.error("Export failed:", err);
            toast.error("Error exportando datos");
        } finally {
            setExporting(false);
        }
    };

    // búsqueda local (sobre todos los items)
    const filtered = useMemo(() => {
        const nq = norm(q);
        if (!nq) return items;
        return items.filter((row) =>
            [
                row.returnId,
                row.chain,
                row.fileName,
                row.date ? new Date(row.date).toLocaleString().replace(",", "") : "",
                // usamos status numérico preferentemente; pero incluimos statusText/results por si acaso
                String(row.status ?? row.Status ?? ""),
                row.statusText ?? row.StatusText ?? "",
                row.results ?? row.Results ?? ""
            ]
                .map(norm)
                .some((v) => v.includes(nq))
        );
    }, [items, q]);

    // paginación local
    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startIdx = (page - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, total);
    const pageRows = filtered.slice(startIdx, endIdx);

    const goPrev = () => setPage((p) => Math.max(1, p - 1));
    const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

    if (loading) {
        return <div className="text-center py-10 text-gray-500">Cargando registros...</div>;
    }

    if (!items.length) {
        return <div className="text-center py-10 text-gray-500">No se encontraron registros</div>;
    }

    return (
        <div className="bg-white rounded-xl shadow p-4">
            {/* resumen + buscador */}
            <div className="flex flex-wrap gap-4 mb-4">
                <div className="px-4 py-2 bg-green-100 text-green-800 rounded-xl text-sm">
                    Carga completa: {summary?.successCount ?? 0}
                </div>
                <div className="px-4 py-2 bg-red-100 text-red-800 rounded-xl text-sm">
                    Pendientes por procesar: {summary?.errorCount ?? 0}
                </div>
                <div className="ml-auto">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            setPage(1); // reset a la página 1 al buscar
                        }}
                        placeholder="Buscar (id, archivo, cadena, status, fecha)"
                        className="border rounded px-3 py-2 text-sm w-72"
                    />
                </div>
                <button
                    onClick={exportToExcel}
                    disabled={exporting}
                    className="bg-green-800 text-white px-3 py-2 rounded text-sm hover:bg-green-900 disabled:opacity-60"
                >
                    {exporting ? "Exportando..." : "Exportar a Excel"}
                </button>
            </div>

            {/* tabla */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                    <thead>
                        {/* Encabezados agrupados */}
                        <tr className="border-b bg-gray-50 text-gray-600">
                            <th colSpan="4" className="py-2 px-3"></th> {/* Este es el encabezado agrupador vacío */}
                            <th colSpan="1" className="py-2 px-3 text-center bg-green-800 text-white">
                                Total Folios
                            </th>
                            <th colSpan="2" className="py-2 px-3 text-center bg-red-800 text-white">
                                Incidencias en la Carga
                            </th>
                            <th colSpan="5" className="py-2 px-3 text-center bg-blue-800 text-white">
                                Cargados Correctamente
                            </th>
                            <th rowSpan="2" className="py-2 px-3"></th>
                        </tr>
                        {/* Encabezados específicos */}
                        <tr className="border-b bg-gray-50 text-gray-600">
                            {/*<th className="py-2 px-3">ID</th>*/}
                            <th className="py-2 px-3">Cadena</th>
                            <th className="py-2 px-3">Archivo</th>
                            <th className="py-2 px-3">Fecha</th>
                            <th className="py-2 px-3">Estatus</th>
                            <th className="py-2 px-3 text-center">En Archivo</th>
                            <th className="py-2 px-3 text-center">Error</th>
                            <th className="py-2 px-3 text-center">Duplicado</th>
                            <th className="py-2 px-3 text-center">Por Clasificar</th>
                            <th className="py-2 px-3 text-center">Retenido</th>
                            <th className="py-2 px-3 text-center">En Aclaración</th>
                            <th className="py-2 px-3 text-center">Cancelado</th>
                            <th className="py-2 px-3 text-center">Clasificado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row) => {
                            const statusNum = row.status ?? row.Status ?? 0;

                            // Conteos por estatus
                            const loaded = row.byClassify ?? row.ByClassify ?? 0;
                            const error = row.error ?? row.Error ?? 0;
                            const duplicate = row.duplicate ?? row.Duplicate ?? 0;
                            const retained = row.retained ?? row.Retained ?? 0;
                            const inClarification = row.inClarification ?? row.InClarification ?? 0;
                            const canceled = row.rejected ?? row.Rejected ?? 0;
                            const classified = row.classified ?? row.Classified ?? 0;

                            const totalFolios =
                                loaded +
                                error +
                                duplicate +
                                retained +
                                inClarification +
                                canceled +
                                classified;

                            return (
                                <tr key={row.returnId} className="border-b hover:bg-gray-50">
                                    {/*<td className="py-2 px-3">{row.returnId}</td>*/}
                                    <td className="py-2 px-3">{row.chain}</td>
                                    <td className="py-2 px-3 break-all whitespace-nowrap">{row.fileName}</td>
                                    <td className="py-2 px-3">
                                        {row.date ? new Date(row.date).toLocaleString().replace(",", "") : "-"}
                                    </td>
                                    <td className="py-2 px-3">
                                        <span
                                            className={`px-2 py-1 rounded text-xs whitespace-nowrap ${error === 0
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-red-100 text-red-700"
                                                }`}
                                        >
                                            {error === 0
                                                ? "Carga completa"
                                                : "Pendientes por procesar"}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-700 text-white text-xs">
                                            {totalFolios}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs">
                                            {error}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-xs">
                                            {duplicate}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">
                                            {loaded}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs">
                                            {retained}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs">
                                            {inClarification}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs">
                                            {canceled}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs">
                                            {classified}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-right h-12 flex items-center justify-end gap-2">
                                        <button
                                            onClick={() => onDetail(row.returnId)}
                                            className="bg-blue-600 text-white px-3 py-0.5 rounded text-xs hover:bg-blue-700"
                                        >
                                            Detalle
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {pageRows.length === 0 && (
                            <tr>
                                <td colSpan="13" className="py-6 text-center text-gray-500">
                                    Sin coincidencias en esta p&aacute;gina.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* footer de paginación (igual que el detalle) */}
            {totalPages > 1 && (
                <div className="flex flex-wrap justify-between items-center gap-3 mt-4 border-t pt-3">
                    <div className="text-sm text-gray-600">
                        Mostrando{" "}
                        <span className="font-medium">{total ? startIdx + 1 : 0}</span> -{" "}
                        <span className="font-medium">{endIdx}</span> de{" "}
                        <span className="font-medium">{total}</span> registros
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        <label className="text-sm text-gray-600">Filas</label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className="border rounded px-2 py-1 text-sm"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>

                        <button
                            disabled={page <= 1}
                            onClick={goPrev}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                        >
                            -
                        </button>

                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">P&aacute;gina</span>
                            <select
                                value={page}
                                onChange={(e) => setPage(Number(e.target.value))}
                                className="border rounded px-2 py-1 text-sm"
                            >
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                            <span className="text-sm text-gray-600">de {totalPages}</span>
                        </div>

                        <button
                            disabled={page >= totalPages}
                            onClick={goNext}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                        >
                            +
                        </button>
                    </div>
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    {loading2 && <LoadingOverlay />}
                    <div className="bg-white rounded-lg w-full max-w-2xl p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-semibold">Autorizar l&iacute;mite - Carga #{modalReturnId}</h3>
                            <div className="flex gap-2">
                                <button className="px-3 py-1 rounded border" onClick={closeModal}>Cerrar</button>
                            </div>
                        </div>

                        {modalLoading ? (
                            <div>Cargando folios…</div>
                        ) : modalFolios.length === 0 ? (
                            <div>No hay folios pendientes (status = 3) para esta carga.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="p-2 text-left">Folio</th>
                                            <th className="p-2 text-right">Importe total</th>
                                            <th className="p-2 text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modalFolios.map((f) => {
                                            const reference = f.reference ?? f.Reference;
                                            const totalAmount = f.totalAmount ?? f.TotalAmount ?? f.total ?? f.Total ?? 0;
                                            return (
                                                <tr key={reference} className="border-t">
                                                    <td className="p-2">{reference}</td>
                                                    <td className="p-2 text-right">
                                                        {Number(totalAmount).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-2 text-right flex gap-2 justify-end">
                                                        <button
                                                            className="px-3 py-1 rounded bg-green-600 text-white text-xs"
                                                            onClick={() => handleAuthorize(reference)}
                                                        >
                                                            Autorizar
                                                        </button>
                                                        <button
                                                            className="px-3 py-1 rounded bg-red-600 text-white text-xs"
                                                            onClick={() => handleReject(reference)}
                                                        >
                                                            Rechazar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DmeLogTable;